/* eslint-disable no-nested-ternary */
import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import {
  signUpPlaidInfo,
  fetchPlaidLinkSettings,
} from '../../actions';
import {
  addNotification,
} from '../../actions/notifications';
import {
  bankLinkDisclaimer,
  bankLinkPlaidInfo,
  bankLinkPlaidEvent,
  createBankLink,
  fetchAccountBankLinks,
} from '../../actions/bankLinks';
import Button from '../Button';
import loadingGif from '../../assets/images/loading.gif';
import ModalContainer from '../BaseModal/ModalContainer';
import { loadModal } from '../../actions/modals';
import { BANK_LINK_DISCLAIMER_MODAL } from '../BaseModal/modalTypes';
import { ALERT_TYPE, dismissAlert } from '../../actions/alerts';

import './BankInfo.css';
import { getConfigs } from '../../actions/configs';
import BaseModal from '../BaseModal';
import CloseIcon from '../../assets/images/close_gray.svg';

const debug = require('debug')('cambria:SignUp:BankInfo');

const PLAID_LINK_EVENT_TYPES = {
  STARTED: 'STARTED',
  OPENED: 'OPENED',
  ENDED: 'ENDED',
  ERROR: 'ERROR',
};

class BankInfo extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isPlaidLinkOpen: false,
      isSubmittingToFolio: false,
      submittedToFolio: false,
      plaidLink: null,
      showBankLinkModal: false,
      bankLinkDisclaimerText: null,
    };
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.bankLinkDisclaimerStatus !== this.props.bankLinkDisclaimerStatus && nextProps.bankLinkDisclaimerStatus === 'ACCEPTED') {
      this.loadAndOpenPlaidLink();
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.configs !== this.props.configs) {
      const { configs } = this.props;
      if (configs && !configs.error && !configs.isFetching) {
        const bankLinkConfig = configs.data.find(i => i.configType === 'bank_link');
        if (
          bankLinkConfig &&
          bankLinkConfig.appConfig
        ) {
          this.handleConfig(bankLinkConfig.appConfig);
        } else {
          this.onBankLinkBtnClick();
        }
      }
    }
  }

  componentWillUnmount() {
    if (this.state.plaidLink) {
      debug('Closing Plaid link');
      this.state.plaidLink.exit();
    }
  }

  onPlaidLinkLoad = () => {
    this.onPlaidLinkEvent(PLAID_LINK_EVENT_TYPES.STARTED);
    debug('Opening Plaid Link');
    if (this.state.plaidLink) {
      this.state.plaidLink.open();
    }
    this.onPlaidLinkEvent(PLAID_LINK_EVENT_TYPES.OPENED);
  }

  onPlaidLinkSuccess = async (publicToken, metadata) => {
    try {
      if (this.props.isSignUp) {
        await this.props.signUpPlaidInfo({ publicToken, metadata });
      } else {
        this.setState({ isSubmittingToFolio: true });
        await this.props.createBankLink(this.props.accountNumber, {
          publicToken,
          metadata,
        });
        this.setState({ isSubmittingToFolio: false });
        await this.props.bankLinkPlaidInfo({ publicToken, metadata });
      }
      this.setState({ isPlaidLinkOpen: false, submittedToFolio: true });
      await this.props.fetchAccountBankLinks(this.props.accountNumber);
      this.onPlaidLinkEvent(PLAID_LINK_EVENT_TYPES.ENDED);
      this.props.alerts
        .filter(alert => alert.type === ALERT_TYPE.BANK_LINK_NOT_ADDED && alert.visible)
        .forEach((alert) => {
          this.props.dismissAlert(alert.id);
        });
      this.props.onNext();
    } catch (err) {
      this.setState({ isSubmittingToFolio: false, submittedToFolio: false, isPlaidLinkOpen: false });
      this.onPlaidLinkEvent(PLAID_LINK_EVENT_TYPES.ERROR, {
        publicToken,
        account_id: metadata.account_id,
        institution_id: metadata.institution.institution_id,
      });
      this.props.addNotification(`Bank Link Error: ${err.message}`);
    }
  };

  onPlaidLinkExit = () => {
    this.onPlaidLinkEvent(PLAID_LINK_EVENT_TYPES.ENDED);
    this.props.bankLinkDisclaimer('DECLINED');
    this.setState({ isPlaidLinkOpen: false });
  };

  onPlaidLinkEvent = async (eventName, metadata) => {
    await this.props.bankLinkPlaidEvent(eventName, metadata);
  };

  onBankLinkBtnClick = () => {
    if (this.props.bankLinkDisclaimerStatus && this.props.bankLinkDisclaimerStatus === 'ACCEPTED') {
      this.loadAndOpenPlaidLink();
    } else {
      this.props.loadModal(BANK_LINK_DISCLAIMER_MODAL);
    }
    this.onPlaidLinkEvent(PLAID_LINK_EVENT_TYPES.ENDED);
    this.setState({ isPlaidLinkOpen: false });
    this.props.onNext();
  }

  handleConfig = (appConfig) => {
    const { bankLink } = appConfig;
    if (bankLink && bankLink.disableInFunnel) {
      this.setState({
        showBankLinkModal: true,
        bankLinkDisclaimerText: bankLink.funnelDisclaimer,
      });
    } else {
      this.onBankLinkBtnClick();
    }
  }

  handleDisclaimerDialog = () => {
    this.setState({ showBankLinkModal: !this.state.showBankLinkModal });
  }

  loadAndOpenPlaidLink = async () => {
    debug('loadAndOpenPlaidLink');
    this.setState({ isPlaidLinkOpen: true });

    if (this.state.plaidLink) {
      this.onPlaidLinkLoad();
      return;
    }

    if (!window.Plaid) {
      if (!this.plaidScript) {
        debug('Inserting Plaid Link UI script');
        this.plaidScript = document.createElement('script');
        this.plaidScript.setAttribute('src', 'https://cdn.plaid.com/link/v2/stable/link-initialize.js');
        document.body.appendChild(this.plaidScript);
      }
      debug('No window.Plaid, retrying in 250 ms');
      setTimeout(this.loadAndOpenPlaidLink, 100);
      return;
    }

    if (!this.plaidLinkSettings) {
      this.plaidLinkSettings = await this.props.fetchPlaidLinkSettings();
    }

    debug('Calling Plaid.create');
    const plaidLink = window.Plaid.create({
      ...this.plaidLinkSettings,
      product: ['auth'],
      onEvent: this.onPlaidLinkEvent,
      onLoad: this.onPlaidLinkLoad,
      onSuccess: this.onPlaidLinkSuccess,
      onExit: this.onPlaidLinkExit,
    });

    this.setState({
      plaidLink,
    });
  }

  removeCurrentBankLink = async () => {
    await this.props.bankLinkPlaidInfo({});
  };

  render() {
    const { plaidInfo, modalType } = this.props;
    const {
      isPlaidLinkOpen,
      isSubmittingToFolio,
      submittedToFolio,
    } = this.state;
    const loadingComponent = isSubmittingToFolio ? (
      <div styleName="overlay">
        <div styleName="center-padded-content">
          <div styleName="linkLoading">
            Please wait until your bank link is created
          </div>
          <div styleName="loadingGifContainer">
            <img
              alt="loading"
              src={loadingGif}
            />
          </div>
        </div>
      </div>) : (
        <div>
          <div styleName="linkLoading">
            Loading ...
          </div>
          <div styleName="loadingGifContainer">
            <img
              alt="loading"
              src={loadingGif}
            />
          </div>
        </div>);
    const plaidLinkView = isPlaidLinkOpen || submittedToFolio ? loadingComponent : (
      <div id="bank-link-info">
        <div styleName="linkInstructions">
          Link a bank to fund your investments.
        </div>
        <div styleName="submitButtonContainer">
          <Button
            onClick={this.props.getConfigs}
          >
            Link bank account
          </Button>
        </div>
      </div>
    );
    return (
      <div className="SignUpBankInfo">
        { plaidInfo && plaidInfo.publicToken && !submittedToFolio ? (
          <div>
            <div styleName="instructions">
              Link a bank to fund your investments.
            </div>
            <div styleName="bankSection">
              <div styleName="titleSection">
                <div>
                  <div styleName="title">
                      Current link
                  </div>
                  <div
                    styleName="linkNew"
                    onClick={this.loadAndOpenPlaidLink}
                    onKeyPress={this.loadAndOpenPlaidLink}
                    role="link"
                    tabIndex={0}
                  >
                    Link different account
                  </div>
                </div>
                <div
                  styleName="removeBankLink"
                  onClick={this.removeCurrentBankLink}
                  onKeyPress={this.removeCurrentBankLink}
                  role="link"
                  tabIndex={0}
                >
                  Remove
                </div>
              </div>
              <div styleName="row">
                <div styleName="bankInfo">
                  Bank
                </div>
                <div styleName="bankInfo">
                  {plaidInfo.metadata.institution.name}
                </div>
              </div>
              <div styleName="row">
                <div styleName="bankInfo">
                  Account
                </div>
                <div styleName="bankInfo">
                  {plaidInfo.metadata.account.name} ({plaidInfo.metadata.account.mask})
                </div>
              </div>
            </div>
            <div styleName="buttonWidthRestriction">
              {isSubmittingToFolio ? loadingComponent : (
                <Button onClick={this.props.onNext}>Continue</Button>
              )}
            </div>
          </div>
        ) : plaidLinkView }
        {
          modalType === 'BANK_LINK_DISCLAIMER_MODAL' ? <ModalContainer /> : null
        }
        { this.state.showBankLinkModal ? (
          <BaseModal onClose={() => this.handleDisclaimerDialog()}>
            <div styleName="dialogHeader">
              <button onClick={() => this.handleDisclaimerDialog()} type="button" styleName="dialogHeaderCloseBtn">
                <img width="12" src={CloseIcon} alt="icon" />
              </button>
            </div>
            <div styleName="dialogTitle">
              Notice
            </div>
            <div styleName="dialogText" style={{ textAlign: 'left' }}>
              <span>
                {this.state.bankLinkDisclaimerText.split('\n').map(text => (<React.Fragment>{text}<br /></React.Fragment>))}
              </span>
            </div>
            <div styleName="dialogActions">
              <Button style={{ border: 'none', WebkitBoxShadow: 'none', boxShadow: 'none' }} onClick={() => this.handleDisclaimerDialog()}>
                Close
              </Button>
            </div>
          </BaseModal>
        ) : null }
      </div>
    );
  }
}

Component.propTypes = {
  isSignUp: PropTypes.bool,
};

Component.defaultProps = {
  isSignUp: true,
};


const mapStateToProps = ({
  authToken,
  signUpInfo,
  bankLinkInfo,
  modals,
  alerts,
  configs,
}, props) => ({
  authToken,
  plaidInfo: props.isSignUp ? get(signUpInfo, 'plaidInfo', {}) : get(bankLinkInfo, 'plaidInfo', {}),
  modalType: get(modals, 'modalType', null),
  bankLinkDisclaimerStatus: get(bankLinkInfo, 'bankLinkDisclaimerStatus', null),
  alerts,
  configs,
});


const mapDispatchToProps = dispatch => ({
  bankLinkDisclaimer: (...args) => (dispatch(bankLinkDisclaimer(...args))),
  signUpPlaidInfo: (...args) => (dispatch(signUpPlaidInfo(...args))),
  bankLinkPlaidInfo: (...args) => (dispatch(bankLinkPlaidInfo(...args))),
  bankLinkPlaidEvent: (...args) => (dispatch(bankLinkPlaidEvent(...args))),
  fetchPlaidLinkSettings: (...args) => (dispatch(fetchPlaidLinkSettings(...args))),
  createBankLink: (...args) => (dispatch(createBankLink(...args))),
  fetchAccountBankLinks: (...args) => (dispatch(fetchAccountBankLinks(...args))),
  addNotification: (...args) => (dispatch(addNotification(...args))),
  loadModal: modelType => dispatch(loadModal(modelType)),
  dismissAlert: id => dispatch(dismissAlert(id)),
  getConfigs: (...args) => (dispatch(getConfigs(...args))),
});


export default connect(mapStateToProps, mapDispatchToProps)(BankInfo);



// WEBPACK FOOTER //
// ./src/components/SignUp/BankInfo.js